import { delay } from "./registry";
import type {
  AppointmentCreateRequest,
  AppointmentDetail,
  AppointmentEvent,
  AppointmentListFilter,
  AppointmentRescheduleRequest,
  AppointmentStatus,
  AppointmentSummary,
  DoctorOption,
  ServiceOption,
} from "../../types/appointment";
import type { PageResponse } from "../../types/patient";

/* ------------------------------------------------------------------ */
/* Seed data                                                          */
/* ------------------------------------------------------------------ */

const DOCTORS: DoctorOption[] = [
  { id: "doc-001", name: "BS. Đỗ Văn Giang", specialty: "Nội tiêu hoá" },
  { id: "doc-002", name: "ThS.BS. Nguyễn Thị Hồng", specialty: "Nhi khoa" },
  { id: "doc-003", name: "BS.CKII. Lê Quang Vinh", specialty: "Tai mũi họng" },
];

const SERVICES: ServiceOption[] = [
  { id: "svc-001", name: "Khám tổng quát", durationMinutes: 20, priceVnd: 200_000 },
  { id: "svc-002", name: "Khám chuyên khoa", durationMinutes: 30, priceVnd: 350_000 },
  { id: "svc-003", name: "Tái khám", durationMinutes: 15, priceVnd: 150_000 },
  { id: "svc-004", name: "Tư vấn dinh dưỡng", durationMinutes: 45, priceVnd: 500_000 },
];

const PATIENT_NAMES: { code: string; name: string; phone: string; dob: string }[] = [
  { code: "BN-AB12CD34", name: "Đỗ Văn Giang",    phone: "+84967369125", dob: "1989-03-15" },
  { code: "BN-EF56GH78", name: "Nguyễn Thị Lan",  phone: "+84934567890", dob: "1995-07-22" },
  { code: "BN-JK90LM12", name: "Trần Văn Hùng",   phone: "+84912345678", dob: "1978-11-04" },
  { code: "BN-NP34QR56", name: "Phạm Thị Mai",    phone: "+84988776655", dob: "2001-05-10" },
  { code: "BN-ST78UV90", name: "Hoàng Văn Nam",   phone: "+84909123456", dob: "1965-08-18" },
  { code: "BN-WX12YZ34", name: "Lê Thị Hoa",      phone: "+84945678123", dob: "1992-02-28" },
  { code: "BN-AB56CD78", name: "Vũ Đình Tâm",     phone: "+84978234567", dob: "1986-12-03" },
  { code: "BN-EF90GH12", name: "Bùi Thị Phượng",  phone: "+84923456789", dob: "1973-06-14" },
];

function makeId() {
  return `appt-${Math.random().toString(36).slice(2, 10)}`;
}

function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Build a realistic schedule for today + the next 6 days. */
function seedAppointments(): AppointmentSummary[] {
  const result: AppointmentSummary[] = [];
  const base = todayMidnight();
  // 7 days × ~10 appointments
  for (let dayOffset = -1; dayOffset <= 6; dayOffset++) {
    const day = new Date(base);
    day.setDate(day.getDate() + dayOffset);
    const dayApptCount = dayOffset === 0 ? 12 : 6 + Math.floor(Math.random() * 6);
    for (let i = 0; i < dayApptCount; i++) {
      const hour = 8 + Math.floor((i * 9) / dayApptCount); // 8:00 → 17:00
      const minute = (i * 13) % 60;
      const start = new Date(day);
      start.setHours(hour, minute, 0, 0);
      // dayOffset starts at -1, so the raw index can be negative; JS `%`
      // keeps the sign of the dividend, which would yield `undefined`.
      const patientIdx =
        (((i + dayOffset * 3) % PATIENT_NAMES.length) + PATIENT_NAMES.length) %
        PATIENT_NAMES.length;
      const patient = PATIENT_NAMES[patientIdx]!;
      const doctor = DOCTORS[i % DOCTORS.length]!;
      const service = SERVICES[i % SERVICES.length]!;
      const status: AppointmentStatus =
        dayOffset < 0
          ? Math.random() < 0.8
            ? "COMPLETED"
            : Math.random() < 0.5
              ? "NO_SHOW"
              : "CANCELLED"
          : dayOffset === 0
            ? hour < new Date().getHours()
              ? "COMPLETED"
              : hour === new Date().getHours()
                ? "CHECKED_IN"
                : Math.random() < 0.7
                  ? "CONFIRMED"
                  : "REQUESTED"
            : Math.random() < 0.6
              ? "CONFIRMED"
              : "REQUESTED";
      result.push({
        id: makeId(),
        patientId: `pat-${patient.code}`,
        patientName: patient.name,
        patientPhone: patient.phone,
        doctorId: doctor.id,
        doctorName: doctor.name,
        serviceId: service.id,
        serviceName: service.name,
        serviceNames: [service.name],
        startAt: start.toISOString(),
        durationMinutes: service.durationMinutes,
        status,
        notes: i % 5 === 0 ? "Bệnh nhân tái khám sau 2 tuần" : null,
        inExam: false,
      });
    }
  }
  return result.sort((a, b) => a.startAt.localeCompare(b.startAt));
}

const STORE: AppointmentSummary[] = seedAppointments();
const EVENTS: Record<string, AppointmentEvent[]> = {};

/* ------------------------------------------------------------------ */
/* Query helpers                                                      */
/* ------------------------------------------------------------------ */

function matchesFilter(a: AppointmentSummary, f: AppointmentListFilter | undefined): boolean {
  if (!f) return true;
  if (f.q && f.q.trim()) {
    const q = f.q.trim().toLowerCase();
    const hay = `${a.patientName} ${a.patientPhone ?? ""} ${a.doctorName} ${a.serviceName ?? ""}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.status && a.status !== f.status) return false;
  if (f.doctorId && a.doctorId !== f.doctorId) return false;
  if (f.fromDate && a.startAt < f.fromDate) return false;
  if (f.toDate) {
    const end = `${f.toDate}T23:59:59.999`;
    if (a.startAt > end) return false;
  }
  return true;
}

/* ------------------------------------------------------------------ */
/* Public mock API                                                    */
/* ------------------------------------------------------------------ */

export const mockAppointments = {
  async list(
    filter: AppointmentListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<AppointmentSummary>> {
    const all = STORE.filter((a) => matchesFilter(a, filter));
    const sort = pageable.sort ?? "startAt";
    const direction = sort.endsWith(",desc") ? -1 : 1;
    all.sort((a, b) => direction * a.startAt.localeCompare(b.startAt));
    const size = pageable.size ?? 20;
    const page = pageable.page ?? 0;
    const start = page * size;
    const content = all.slice(start, start + size);
    return delay({
      content,
      totalElements: all.length,
      totalPages: Math.max(1, Math.ceil(all.length / size)),
      number: page,
      size,
    });
  },

  async forDate(date: string): Promise<AppointmentSummary[]> {
    const result = STORE.filter((a) => a.startAt.startsWith(date)).sort((a, b) =>
      a.startAt.localeCompare(b.startAt)
    );
    return delay(result);
  },

  async forWeek(weekStart: Date): Promise<AppointmentSummary[]> {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    const result = STORE.filter((a) => {
      const d = new Date(a.startAt);
      return d >= start && d < end;
    }).sort((a, b) => a.startAt.localeCompare(b.startAt));
    return delay(result);
  },

  async get(id: string): Promise<AppointmentDetail> {
    const a = STORE.find((x) => x.id === id);
    if (!a) throw new Error("Không tìm thấy lịch hẹn");
    const patient = PATIENT_NAMES.find((p) => `pat-${p.code}` === a.patientId);
    const events = EVENTS[id] ?? [
      {
        occurredAt: a.startAt,
        actor: "Lễ tân",
        action: "appointment.create",
        toStatus: "REQUESTED",
      },
    ];
    return delay({
      ...a,
      patientCode: patient?.code ?? null,
      patientDateOfBirth: patient?.dob ?? null,
      createdAt: a.startAt,
      updatedAt: new Date().toISOString(),
      events,
    });
  },

  async create(req: AppointmentCreateRequest): Promise<AppointmentDetail> {
    const doctor = DOCTORS.find((d) => d.id === req.doctorId);
    const service = SERVICES.find((s) => s.id === req.serviceId);
    const patient = PATIENT_NAMES.find((p) => `pat-${p.code}` === req.patientId) ?? {
      code: req.patientId,
      name: "Bệnh nhân",
      phone: null,
      dob: null,
    };
    const fresh: AppointmentSummary = {
      id: makeId(),
      patientId: req.patientId,
      patientName: patient.name,
      patientPhone: patient.phone,
      doctorId: req.doctorId ?? "",
      doctorName: doctor?.name ?? "Bác sĩ bất kỳ",
      serviceId: req.serviceId ?? null,
      serviceName: service?.name ?? null,
      startAt: req.startAt,
      durationMinutes: req.durationMinutes,
      status: "REQUESTED",
      notes: req.notes ?? null,
      inExam: false,
    };
    STORE.push(fresh);
    EVENTS[fresh.id] = [
      {
        occurredAt: new Date().toISOString(),
        actor: "Lễ tân",
        action: "appointment.create",
        toStatus: "REQUESTED",
      },
    ];
    return this.get(fresh.id);
  },

  async transition(id: string, to: AppointmentStatus, note?: string): Promise<void> {
    const a = STORE.find((x) => x.id === id);
    if (!a) throw new Error("Không tìm thấy lịch hẹn");
    const from = a.status;
    a.status = to;
    EVENTS[id] = [
      ...(EVENTS[id] ?? []),
      {
        occurredAt: new Date().toISOString(),
        actor: "Lễ tân",
        action: "appointment.transition",
        fromStatus: from,
        toStatus: to,
        note,
      },
    ];
    return delay(undefined);
  },

  async remove(id: string): Promise<void> {
    const i = STORE.findIndex((x) => x.id === id);
    if (i >= 0) STORE.splice(i, 1);
    delete EVENTS[id];
    return delay(undefined);
  },

  async reschedule(id: string, req: AppointmentRescheduleRequest): Promise<void> {
    const a = STORE.find((x) => x.id === id);
    if (!a) throw new Error("Không tìm thấy lịch hẹn");
    EVENTS[id] = [
      ...(EVENTS[id] ?? []),
      {
        occurredAt: new Date().toISOString(),
        actor: "Lễ tân",
        action: "appointment.reschedule",
        note: `${a.startAt} → ${req.startAt}` + (req.reason ? ` (${req.reason})` : ""),
      },
    ];
    a.startAt = req.startAt;
    a.durationMinutes = req.durationMinutes;
    return delay(undefined);
  },

  async listDoctors(): Promise<DoctorOption[]> {
    return delay(DOCTORS);
  },

  async listServices(): Promise<ServiceOption[]> {
    return delay(SERVICES);
  },

  async searchPatients(q: string): Promise<{
    id: string;
    name: string;
    code: string;
    phone: string | null;
    dob: string | null;
  }[]> {
    const needle = q.trim().toLowerCase();
    if (!needle) return delay([]);
    const hits = PATIENT_NAMES.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        (p.phone ?? "").includes(needle) ||
        p.code.toLowerCase().includes(needle)
    );
    return delay(
      hits.slice(0, 8).map((p) => ({
        id: `pat-${p.code}`,
        name: p.name,
        code: p.code,
        phone: p.phone,
        dob: p.dob,
      }))
    );
  },
};
